import "../../src/configureAmplify";
import db from "../aws/dynamodb";
import { RUTTER_CONNECTIONS_TABLE_NAME } from "../constants";

export type ConnectedMarketingChannels = {
  isShopifyStoreConnected: boolean;
  isWoocommerceStoreConnected: boolean;
  isMagentoStoreConnected: boolean;
  isBigCommerceStoreConnected: boolean;
};

export async function saveShopifyAccessToken({
  username,
  accessToken,
  storeId,
}: {
  username?: string;
  accessToken: string;
  storeId: string;
}) {
  try {
    return await db.update({
      TableName: RUTTER_CONNECTIONS_TABLE_NAME,
      Key: {
        storeId,
      },
      UpdateExpression: `
      SET 
        shopifyAccessToken = :accessToken,
        username = if_not_exists(username, :username),
        createdAt = if_not_exists(createdAt, :createdAt),
        updatedAt = :updatedAt
    `,
      ExpressionAttributeValues: {
        ":accessToken": accessToken,
        ":username": username || "",
        ":createdAt": new Date().toISOString(),
        ":updatedAt": new Date().toISOString(),
      },
      ReturnValues: "UPDATED_NEW",
    });
  } catch (error) {
    console.error("ERROR SAVING SHOPIFY ACCESS TOKEN ", error);
    throw new Error("Error saving Shopify access token");
  }
}

export async function saveWoocommerceAccessToken({
  username,
  accessToken,
  storeId,
}: {
  username: string;
  accessToken: string;
  storeId: string;
}) {
  const { Item } = await db.get({
    TableName: RUTTER_CONNECTIONS_TABLE_NAME,
    Key: {
      storeId,
    },
  });

  if (Item) {
    return db.update({
      TableName: RUTTER_CONNECTIONS_TABLE_NAME,
      Key: {
        storeId,
      },
      UpdateExpression: "set woocommerceAccessToken = :accessToken, updatedAt = :updatedAt",
      ExpressionAttributeValues: {
        ":accessToken": accessToken,
        ":updatedAt": new Date().toISOString(),
      },
      ReturnValues: "UPDATED_NEW",
    });
  }

  const item = {
    storeId,
    username,
    woocommerceAccessToken: accessToken,
    createdAt: new Date().toISOString(),
  };

  await db.put({ TableName: RUTTER_CONNECTIONS_TABLE_NAME, Item: item });
}

export async function saveMagentoAccessToken({
  username,
  accessToken,
  storeId,
}: {
  username: string;
  accessToken: string;
  storeId: string;
}) {
  return db.update({
    TableName: RUTTER_CONNECTIONS_TABLE_NAME,
    Key: {
      storeId,
    },
    UpdateExpression:
      "set magentoAccessToken = :accessToken, updatedAt = :updatedAt, username = :username",
    ExpressionAttributeValues: {
      ":accessToken": accessToken,
      ":username": username,
      ":updatedAt": new Date().toISOString(),
    },
    ReturnValues: "UPDATED_NEW",
  });
}

export async function saveBigCommerceAccessToken({
  username,
  accessToken,
  storeId,
}: {
  username: string;
  accessToken: string;
  storeId: string;
}) {
  return db.update({
    TableName: RUTTER_CONNECTIONS_TABLE_NAME,
    Key: {
      storeId,
    },
    UpdateExpression:
      "set bigCommerceAccessToken = :accessToken, updatedAt = :updatedAt, username = :username",
    ExpressionAttributeValues: {
      ":accessToken": accessToken,
      ":username": username,
      ":updatedAt": new Date().toISOString(),
    },
    ReturnValues: "UPDATED_NEW",
  });
}

export async function getConnectedStores(storeId: string): Promise<ConnectedMarketingChannels> {
  const { Item } = await db.get({
    TableName: RUTTER_CONNECTIONS_TABLE_NAME,
    Key: {
      storeId,
    },
  });

  return {
    isShopifyStoreConnected: Item?.shopifyAccessToken !== undefined,
    isWoocommerceStoreConnected: Item?.woocommerceAccessToken !== undefined,
    isMagentoStoreConnected: Item?.magentoAccessToken !== undefined,
    isBigCommerceStoreConnected: Item?.bigCommerceAccessToken !== undefined,
  };
}

export async function getConnectedStoreAccessToken(storeId: string): Promise<string | null> {
  const { Item } = await db.get({
    TableName: RUTTER_CONNECTIONS_TABLE_NAME,
    Key: {
      storeId,
    },
  });

  if (Item?.shopifyAccessToken) {
    return Item.shopifyAccessToken;
  }

  if (Item?.woocommerceAccessToken) {
    return Item.woocommerceAccessToken;
  }

  if (Item?.magentoAccessToken) {
    return Item.magentoAccessToken;
  }

  if (Item?.bigCommerceAccessToken) {
    return Item.bigCommerceAccessToken;
  }

  return null;
}

export async function deleteRutterConnectionsRecord(storeId: string) {
  try {
    db.delete({
      TableName: RUTTER_CONNECTIONS_TABLE_NAME,
      Key: {
        storeId,
      },
    });
    return `Deleted Rutter Connections record for storeId: ${storeId}`;
  } catch (error) {
    console.error("DELETING RUTTER CONNECTIONS ERROR ", error);
    return `Error deleting Rutter Connections record for storeId: ${storeId}`;
  }
}

export async function storeShopifyRutterConnection({
  storeId,
  rutterAccessToken,
}: {
  storeId: string;
  rutterAccessToken: string;
}) {
  try {
    await db.put({
      TableName: RUTTER_CONNECTIONS_TABLE_NAME,
      Item: {
        storeId,
        shopifyAccessToken: rutterAccessToken,
        createdAt: new Date().toISOString(),
        isShopifyAppInstalled: true,
      },
    });
  } catch (error) {
    console.error("Error storing Shopify Rutter connection:", error);
  }
}
